import { useEffect, useRef, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Check, ChevronDown, CircleUser, Gavel, Heart, LayoutList, Package, Search, TrendingUp } from "lucide-react";
import { useAuctions } from "../contexts/AuctionsContext";
import { platformLastOrganizationKey, usePlatformAuth } from "../lib/use-platform-auth";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { BottomNav } from "./BottomNav";

const logoLight = "/branding/logo-light.svg";

export function AppLayout() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { liveCount } = useAuctions();
  const { organizations, sessionData } = usePlatformAuth();

  const base = `/${organizationSlug}`;
  const currentOrganization =
    organizations.find((organization) => organization.slug === organizationSlug) ?? null;
  const hasMultipleOrganizations = organizations.length > 1;

  useEffect(() => {
    const userId = sessionData?.user?.id;
    if (!userId || !currentOrganization?.slug || typeof window === "undefined") {
      return;
    }

    window.sessionStorage.setItem(platformLastOrganizationKey(userId), currentOrganization.slug);
  }, [currentOrganization?.slug, sessionData?.user?.id]);

  // Redirect to root when the URL slug doesn't match any of the user's organizations.
  // This catches cases like navigating to /budrunder instead of /{org-slug}/budrunder.
  if (organizations.length > 0 && !currentOrganization) {
    return <Navigate to="/" replace />;
  }

  const tabs = [
    { to: `${base}/budrunder`, label: "Budrunder", icon: Gavel, count: liveCount },
    { to: `${base}/mine-bud`, label: "Mine bud", icon: TrendingUp, count: null },
    { to: `${base}/favoritter`, label: "Favoritter", icon: Heart, count: null },
    { to: `${base}/mine-kjop`, label: "Mine kjøp", icon: Package, count: null },
    { to: `${base}/mine-auksjoner`, label: "Mine auksjoner", icon: LayoutList, count: null },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top navbar — Responsive header */}
      <header
        className="sticky top-0 z-40 bg-[#f9fafb] border-b border-border shadow-sm"
      >
        <div className="mx-auto flex h-14 lg:h-16 max-w-screen-xl items-center px-4 lg:px-6">
          {/* Logo */}
          <NavLink to={`${base}/budrunder`} className="mr-8 flex flex-shrink-0 items-center gap-3 group">
            <img
              src={logoLight}
              alt="Forhandlerbørsen"
              className="h-12 w-auto transition-transform duration-200 group-hover:scale-[1.02] lg:h-14"
            />
          </NavLink>

          {/* Desktop Tabs */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-2">
            {tabs.map(({ to, label, icon: Icon, count }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `
                  relative flex flex-col items-center justify-center min-w-[90px] h-[72px] gap-1 px-4 transition-all duration-200
                  ${isActive
                    ? "text-accent bg-white border-[1.5px] border-accent rounded-xl shadow-sm z-10 scale-105"
                    : "text-[#6b7280] hover:text-[#1a2e35] hover:bg-white/50 rounded-xl"
                  }
                `}
              >
                {({ isActive }) => (
                  <>
                    <div className="relative">
                      <Icon className={`h-5 w-5 ${isActive ? 'text-accent' : ''}`} />
                      {count !== null && count > 0 && (
                        <span
                          className="absolute -right-2.5 -top-2 flex min-w-[18px] h-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white shadow-sm"
                          style={{ background: "var(--accent)" }}
                        >
                          {count}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] font-bold tracking-tight">{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <NavLink
              to={`${base}/konto`}
              className="flex flex-col items-center justify-center gap-1 px-4 py-2 text-[#6b7280] transition-all hover:text-[#1a2e35]"
            >
              <CircleUser className="h-5 w-5" />
              <span className="text-[11px] font-bold">Min konto</span>
            </NavLink>

            {hasMultipleOrganizations && currentOrganization ? (
              <OrganizationSwitcher
                currentOrganizationSlug={organizationSlug}
                currentPath={location.pathname}
                organizations={organizations}
                onSelect={async (nextOrganizationSlug) => {
                  const currentSegments = location.pathname.split("/").filter(Boolean);
                  const nextSegments = [...currentSegments];

                  if (nextSegments.length > 0) {
                    nextSegments[0] = nextOrganizationSlug;
                  } else {
                    nextSegments.push(nextOrganizationSlug);
                  }

                  navigate(`/${nextSegments.join("/")}`);
                }}
              />
            ) : null}
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 pb-20 lg:pb-0">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-[#f9fafb] mb-20 lg:mb-0">
        <div className="mx-auto flex max-w-screen-xl flex-wrap items-center justify-center gap-x-6 gap-y-1 px-4 py-4 text-xs text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} Forhandlerbørsen</span>
          <a href="https://forhandlerborsen.no/personvern" target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors">Personvern</a>
          <a href="https://forhandlerborsen.no/cookies" target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors">Cookies</a>
          <a href="https://forhandlerborsen.no/vilkar" target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors">Vilkår</a>
        </div>
      </footer>

      {/* Mobile Bottom Nav */}
      <BottomNav />
    </div>
  );
}

function OrganizationSwitcher({
  currentOrganizationSlug,
  currentPath,
  organizations,
  onSelect,
}: {
  currentOrganizationSlug?: string;
  currentPath: string;
  organizations: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
  }>;
  onSelect: (slug: string, id: string) => Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const currentOrganization =
    organizations.find((organization) => organization.slug === currentOrganizationSlug) ??
    organizations[0];

  const currentSubPath = currentPath.split("/").filter(Boolean).slice(1).join("/");
  const initials = currentOrganization.name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const filtered = (search.trim()
    ? organizations.filter((o) =>
        o.name.toLowerCase().includes(search.toLowerCase()) ||
        o.slug.toLowerCase().includes(search.toLowerCase())
      )
    : organizations
  ).slice().sort((a, b) => a.name.localeCompare(b.name, "nb"));

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all hover:bg-white/80 border border-transparent hover:border-border shadow-sm active:scale-95"
          style={{
            background: "white",
            border: "1px solid var(--border)",
          }}
        >
          <div
            className="flex h-7 w-7 items-center justify-center rounded-md text-[11px] font-black text-white"
            style={{ background: "var(--primary)" }}
          >
            {initials}
          </div>
          <div className="hidden min-w-0 text-left sm:block">
            <div className="max-w-[140px] truncate text-xs font-black text-primary uppercase tracking-tight">{currentOrganization.name}</div>
            <div className="truncate text-[10px] font-bold text-muted-foreground uppercase opacity-70">
              {currentOrganization.role}
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 rounded-xl border-none bg-white p-2 shadow-[0_24px_60px_rgba(0,31,30,0.18)]"
        onOpenAutoFocus={(e) => { e.preventDefault(); setTimeout(() => searchRef.current?.focus(), 0); }}
      >
        <div className="px-1 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søk organisasjoner..."
              className="w-full rounded-lg border border-border bg-muted/50 py-2 pl-8 pr-3 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-primary/30 focus:ring-1 focus:ring-primary/20"
            />
          </div>
        </div>
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              Ingen treff
            </div>
          ) : (
            filtered.map((organization) => {
              const organizationInitials = organization.name
                .split(" ")
                .map((word) => word[0])
                .join("")
                .slice(0, 2)
                .toUpperCase();
              const isCurrent = organization.slug === currentOrganization.slug;

              return (
                <button
                  key={organization.id}
                  type="button"
                  onClick={(e) => {
                    setOpen(false);
                    setSearch("");
                    if (e.metaKey || e.ctrlKey) {
                      const segments = currentPath.split("/").filter(Boolean);
                      segments[0] = organization.slug;
                      window.open(`/${segments.join("/")}`, "_blank");
                    } else {
                      void onSelect(organization.slug, organization.id);
                    }
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-[rgba(255,90,31,0.06)]"
                >
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-md text-xs font-bold"
                    style={{
                      background: isCurrent ? "var(--primary)" : "var(--muted)",
                      color: isCurrent ? "white" : "var(--foreground)",
                    }}
                  >
                    {organizationInitials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {organization.name}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {organization.role}
                    </div>
                  </div>
                  {isCurrent ? <Check className="h-4 w-4 text-primary" /> : null}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
